// CommunityHandler.js - Extracted community/forum logic from App.js
import React, { useCallback } from 'react';
import { useNotification } from '../contexts/NotificationContext';
import { useCommunity } from '../contexts/CommunityContext';
import { useForm } from '../contexts/FormContext';
import { useCommunityData } from '../hooks/useCommunityData';
import { createMovieSlug, createReleaseSlug } from '../utils/urlUtils';

export const useCommunityHandler = (supabase, currentUser, userProfile) => {
  const { showSuccess, showError } = useNotification();
  const community = useCommunity();
  const form = useForm();
  const communityData = useCommunityData(supabase, currentUser);

  // Request caching to prevent duplicate API calls
  const [loadingStates, setLoadingStates] = React.useState({
    communityData: false,
    communityStats: false,
  });
  const requestCacheRef = React.useRef({
    lastStatsLoad: 0,
    lastDataLoad: 0,
  });

  // Helper function to format time
  const formatTimeAgo = timestamp => {
    const now = new Date();
    const past = new Date(timestamp);
    const diffInMinutes = Math.floor((now - past) / (1000 * 60));

    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes} min${diffInMinutes > 1 ? 's' : ''} ago`;

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;

    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
  };

  const loadCommunityStats = useCallback(async () => {
    // Prevent duplicate requests within 30 seconds
    const now = Date.now();
    const CACHE_DURATION = 30000; // 30 seconds

    // Use current value to avoid dependency issues
    if (loadingStates.communityStats) {
      console.log('⏸️ Community stats already loading, skipping request');
      return;
    }

    if (now - requestCacheRef.current.lastStatsLoad < CACHE_DURATION) {
      console.log('📦 Using cached community stats, skipping request');
      return;
    }

    try {
      setLoadingStates(prev => ({ ...prev, communityStats: true }));
      requestCacheRef.current.lastStatsLoad = now;

      // Count total users
      const { count: userCount } = await supabase
        .from('user_profiles')
        .select('*', { count: 'exact', head: true });

      // Count active trade listings
      const { count: tradeCount } = await supabase
        .from('trade_listings')
        .select('*', { count: 'exact', head: true })
        .eq('is_active', true);

      // Count recent forum posts
      const { count: postCount } = await supabase
        .from('forum_posts')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString());

      // Count total forum replies
      const { count: replyCount } = await supabase
        .from('forum_replies')
        .select('*', { count: 'exact', head: true });

      community.setCommunityStats({
        totalUsers: userCount || 0,
        activeTrades: tradeCount || 0,
        recentPosts: postCount || 0,
        totalReplies: replyCount || 0,
        challengeProgress: 0, // Will be real when you add community.challenges table
      });
    } catch (error) {
      console.error('Error loading community stats:', error);
    } finally {
      setLoadingStates(prev => ({ ...prev, communityStats: false }));
    }
  }, [supabase, community]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadTradeListings = useCallback(async () => {
    try {
      // First get trade listings
      const { data: trades, error } = await supabase
        .from('trade_listings')
        .select('*')
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error loading trade listings:', error);
        throw error;
      }

      if (!trades || trades.length === 0) {
        community.setTradeRequests([]);
        return;
      }

      // Collect all release IDs and master item IDs across all offer and want items
      const allOfferItems = trades.flatMap(t =>
        Array.isArray(t.trade_offer_items) && t.trade_offer_items.length > 0
          ? t.trade_offer_items
          : [{ release_id: t.release_id || null, item_id: t.item_id || null }]
      );
      const allWantItems = trades.flatMap(t =>
        Array.isArray(t.trade_want_items) ? t.trade_want_items : []
      );
      const releaseIds = [...new Set(allOfferItems.map(i => i.release_id).filter(Boolean))];
      const masterItemIds = [
        ...new Set([
          ...allOfferItems.filter(i => !i.release_id && i.item_id).map(i => i.item_id),
          ...allWantItems.filter(i => i.item_id).map(i => i.item_id),
        ]),
      ];
      const traderIds = [...new Set(trades.map(t => t.trader_id).filter(Boolean))];

      // Get release data with movie info including genre (only if there are release IDs)
      let releases = [];
      if (releaseIds.length > 0) {
        const { data: releaseData, error: releaseError } = await supabase
          .from('movie_releases')
          .select(
            `
            id,
            display_id,
            release_title,
            media_titles!master_movie_id (
              id,
              display_id,
              title,
              year,
              genre,
              director
            )
          `
          )
          .in('id', releaseIds);

        if (releaseError) {
          console.error('Error loading releases:', releaseError);
        } else {
          releases = releaseData || [];
        }
      }

      // Get master-only movie data for trades without a specific release
      let masterTitles = [];
      if (masterItemIds.length > 0) {
        const { data: titleData, error: titleError } = await supabase
          .from('media_titles')
          .select('id, display_id, title, year, genre, director')
          .in('id', masterItemIds);

        if (titleError) {
          console.error('Error loading master titles:', titleError);
        } else {
          masterTitles = titleData || [];
        }
      }

      // Get trader usernames and avatars
      const { data: traders, error: traderError } = await supabase
        .from('user_profiles')
        .select('user_id, username, avatar_url')
        .in('user_id', traderIds);

      if (traderError) {
        console.error('Error loading trader profiles:', traderError);
      }

      // Create maps for release data, master title data, and trader data
      const releaseMap = {};
      releases?.forEach(release => {
        releaseMap[release.id] = release;
      });

      const masterTitleMap = {};
      masterTitles?.forEach(title => {
        masterTitleMap[title.id] = title;
      });

      const traderMap = {};
      traders?.forEach(trader => {
        traderMap[trader.user_id] = {
          username: trader.username,
          avatar_url: trader.avatar_url,
        };
      });

      // Helper: resolve a single {release_id, item_id} to a catalog URL
      const resolveOfferSlug = ({ release_id, item_id }) => {
        const rel = release_id ? releaseMap[release_id] : null;
        if (rel) {
          return {
            url: `/catalog/release/${createReleaseSlug(rel.media_titles, rel)}`,
          };
        }
        const master = item_id ? masterTitleMap[item_id] : null;
        if (master) {
          return { url: `/catalog/${createMovieSlug(master)}` };
        }
        return null;
      };

      // Transform data to match expected format
      const transformedTrades = trades.map(trade => {
        const release = releaseMap[trade.release_id];
        const masterTitle = !release && trade.item_id ? masterTitleMap[trade.item_id] : null;
        let movieTitle, year, genre, director, releaseTitle, fullTitle;

        if (release) {
          movieTitle = release?.media_titles?.title || 'Unknown Title';
          year = release?.media_titles?.year;
          genre = release?.media_titles?.genre;
          director = release?.media_titles?.director;
          releaseTitle = release?.release_title || 'Standard Release';
          fullTitle = movieTitle;
          if (year) fullTitle += ` (${year})`;
          if (releaseTitle && releaseTitle.trim() && releaseTitle !== 'Standard Release') {
            fullTitle += ` - ${releaseTitle}`;
          }
        } else if (masterTitle) {
          movieTitle = masterTitle.title || 'Unknown Title';
          year = masterTitle.year;
          genre = masterTitle.genre;
          director = masterTitle.director;
          releaseTitle = 'General Trade';
          fullTitle = movieTitle;
          if (year) fullTitle += ` (${year})`;
        } else {
          fullTitle = trade.trade_offers || 'General Trade Request';
          movieTitle = fullTitle;
          releaseTitle = 'General Trade';
        }

        // Build per-offer slug array from trade_offer_items if present, else fall back to legacy single IDs
        const offerItemSources =
          Array.isArray(trade.trade_offer_items) && trade.trade_offer_items.length > 0
            ? trade.trade_offer_items
            : [{ release_id: trade.release_id || null, item_id: trade.item_id || null }];

        const offerSlugs = offerItemSources.map(src => resolveOfferSlug(src));

        return {
          id: trade.id,
          display_id: trade.display_id,
          user_id: trade.trader_id,
          user_name: traderMap[trade.trader_id]?.username || 'Unknown User',
          avatar_url: traderMap[trade.trader_id]?.avatar_url || null,
          movie_title: fullTitle,
          release_title: releaseTitle,
          genre,
          director,
          year,
          wants: trade.trade_wants ? trade.trade_wants.split(', ').map(item => item.trim()) : [],
          offers: trade.trade_offers ? trade.trade_offers.split(', ').map(item => item.trim()) : [],
          created_at: trade.created_at,
          time: formatTimeAgo(trade.created_at),
          collection_item_id: trade.collection_item_id,
          release_id: trade.release_id,
          item_id: trade.item_id,
          trade_images: trade.trade_images || [],
          offer_slugs: offerSlugs,
          want_slugs: Array.isArray(trade.trade_want_items)
            ? trade.trade_want_items.map(w =>
                w.item_id && masterTitleMap[w.item_id]
                  ? { url: `/catalog/${createMovieSlug(masterTitleMap[w.item_id])}` }
                  : null
              )
            : [],
        };
      });

      // Set trade requests in community state
      community.setTradeRequests(transformedTrades);

      // Trade activities are now loaded as part of the main activity feed in useCommunityData.js
    } catch (error) {
      console.error('Error loading trade listings:', error);
    }
  }, [supabase, community]);

  const loadCommunityData = useCallback(async () => {
    // Prevent duplicate requests within 15 seconds
    const now = Date.now();
    const CACHE_DURATION = 15000; // 15 seconds

    // Use current value to avoid dependency issues
    if (loadingStates.communityData) {
      // Skip if already loading
      return;
    }

    if (now - requestCacheRef.current.lastDataLoad < CACHE_DURATION) {
      // Using cached data
      return;
    }

    try {
      setLoadingStates(prev => ({ ...prev, communityData: true }));
      requestCacheRef.current.lastDataLoad = now;

      // Load forum posts with a separate query for user data
      const { data: posts, error: postsError } = await supabase
        .from('forum_posts')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(20);

      if (!postsError && posts) {
        // Get user profiles for all the posts
        const userIds = [...new Set(posts.map(post => post.user_id).filter(Boolean))];
        const { data: profiles } =
          userIds.length > 0
            ? await supabase
                .from('user_profiles')
                .select('user_id, username, avatar_url')
                .in('user_id', userIds)
            : { data: [] };

        // Create a map of user_id to user data
        const userMap = {};
        profiles?.forEach(profile => {
          userMap[profile.user_id] = {
            username: profile.username,
            avatar_url: profile.avatar_url,
          };
        });

        const formattedPosts = posts.map(post => {
          const userData = post.user_id
            ? userMap[post.user_id] || { username: 'Unknown User', avatar_url: null }
            : { username: '[deleted]', avatar_url: null };
          return {
            id: post.id,
            title: post.title,
            author: userData.username,
            avatar_url: userData.avatar_url,
            category: post.category,
            content: post.content,
            replies: post.replies || 0,
            time: formatTimeAgo(post.created_at),
          };
        });

        community.setForumPosts(formattedPosts);
      } else if (postsError) {
        console.error('Error loading forum posts:', postsError);
      }

      // Load community stats using new hook
      const stats = await communityData.loadCommunityStats();
      if (stats) {
        community.setCommunityStats(stats);
      }

      // Load real activity feed using new hook
      const activityFeed = await communityData.loadActivityFeed();
      community.setActivityFeed(activityFeed);

      // Load trade requests using local function
      await loadTradeListings();

      // Load challenges using new hook
      const challenges = await communityData.loadChallenges();
      community.setChallenges(challenges);
    } catch (error) {
      console.error('Error loading community data:', error);
    } finally {
      setLoadingStates(prev => ({ ...prev, communityData: false }));
    }
  }, [supabase, community, communityData, formatTimeAgo]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleNewForumPost = async () => {
    if (
      !form.postForm.title.trim() ||
      !form.postForm.content.trim() ||
      !currentUser ||
      !userProfile
    ) {
      if (!currentUser) showError('Please log in to post');
      if (!userProfile) showError('Please complete your profile setup first');
      if (!form.postForm.title.trim()) showError('Please enter a post title');
      return;
    }

    try {
      // Save to database without the join
      const { data: newPost, error } = await supabase
        .from('forum_posts')
        .insert({
          user_id: currentUser.id,
          title: form.postForm.title.trim(),
          content: form.postForm.content,
          category: form.selectedForumCategory,
          replies: 0,
        })
        .select()
        .single();

      if (error) {
        console.error('Error saving forum post:', error);
        showError('Error saving post: ' + error.message);
        return;
      }

      // Update local state with the new post (using userProfile data we already have)
      const formattedPost = {
        id: newPost.id,
        title: newPost.title,
        author: userProfile.username, // Use the userProfile we already have
        category: newPost.category,
        content: newPost.content,
        replies: newPost.replies,
        time: 'Just now',
      };

      community.addForumPost(formattedPost);
      form.setPostContent('');
      form.setPostTitle('');

      // Add to activity feed
      const newActivity = {
        id: Date.now(),
        user: userProfile.username,
        action: 'started discussion',
        item: newPost.title,
        time: 'Just now',
        type: 'forum',
      };
      community.addActivity(newActivity);

      console.log('Forum post saved successfully!');
    } catch (error) {
      console.error('Error creating forum post:', error);
      showError('Error creating post: ' + error.message);
    }
  };

  const handleNewTradeRequest = async tradeRequest => {
    try {
      console.log('CommunityHandler: Processing trade request:', tradeRequest);

      // Save to database first
      const insertData = {
        trader_id: tradeRequest.user_id,
        trade_wants: tradeRequest.wants ? tradeRequest.wants.join(', ') : 'open to offers',
        trade_offers: tradeRequest.offers
          ? tradeRequest.offers.join(', ')
          : tradeRequest.movie_title || 'Not specified', // Use movie_title for collection listings
        is_active: true,
        trade_images: tradeRequest.trade_images || [],
      };

      if (tradeRequest.trade_want_items && tradeRequest.trade_want_items.length > 0) {
        insertData.trade_want_items = tradeRequest.trade_want_items;
      }
      if (tradeRequest.trade_offer_items && tradeRequest.trade_offer_items.length > 0) {
        insertData.trade_offer_items = tradeRequest.trade_offer_items;
      }

      // Only add these fields if they exist (for specific collection item trades)
      if (tradeRequest.collection_item_id) {
        insertData.collection_item_id = tradeRequest.collection_item_id;
      }
      if (tradeRequest.release_id) {
        insertData.release_id = tradeRequest.release_id;
      }
      if (tradeRequest.item_id) {
        insertData.item_id = tradeRequest.item_id;
      }

      console.log('CommunityHandler: Inserting data:', insertData);

      const { data, error } = await supabase.from('trade_listings').insert([insertData]).select();

      if (error) {
        console.error('CommunityHandler: Database error:', error);
        console.error('Failed insert data:', insertData);

        // Provide more helpful error message
        if (error.code === '23503') {
          throw new Error(`Foreign key constraint violation: ${error.message}`);
        }
        throw new Error(`Database error: ${error.message || 'Unknown error'}`);
      }

      const savedTrade = data[0];

      // Add the trade request to the community state with database ID
      const tradeWithId = {
        ...tradeRequest,
        id: savedTrade.id,
        created_at: savedTrade.created_at,
      };
      community.addTradeRequest(tradeWithId);

      // Reload trade listings to refresh the UI
      await loadTradeListings();
    } catch (error) {
      console.error('Error saving trade request:', error);
      throw error; // Re-throw to show error to user
    }
  };

  return {
    loadCommunityData,
    loadCommunityStats,
    loadTradeListings,
    handleNewForumPost,
    handleNewTradeRequest,
    formatTimeAgo,
  };
};
